/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ZOOM_CONFIG } from '../constants';
import {
  UseTimelineZoomParams,
  UseTimelineZoomReturn,
  ViewWindow,
} from '../types';

export function useTimelineZoom({
  totalStart,
  totalEnd,
  onRequestExtendBounds,
}: UseTimelineZoomParams): UseTimelineZoomReturn {
  const [view, setView] = useState<ViewWindow>({
    start: totalStart,
    end: totalEnd,
  });

  const [pendingExt, setPendingExt] = useState<{
    past: number;
    future: number;
  }>({ past: 0, future: 0 });
  const pendingExtRef = useRef(pendingExt);
  useEffect(() => {
    pendingExtRef.current = pendingExt;
  }, [pendingExt]);

  const prevTotalStartRef = useRef(totalStart);
  const prevTotalEndRef = useRef(totalEnd);

  useEffect(() => {
    const prevS = prevTotalStartRef.current;
    const prevE = prevTotalEndRef.current;
    prevTotalStartRef.current = totalStart;
    prevTotalEndRef.current = totalEnd;

    if (prevS === totalStart && prevE === totalEnd) return;

    const hasPendingExt =
      pendingExtRef.current.past > 0 || pendingExtRef.current.future > 0;

    if (hasPendingExt) {
      const deltaPast = Math.max(0, prevS - totalStart);
      const deltaFuture = Math.max(0, totalEnd - prevE);
      const prevPendingExt = pendingExtRef.current;
      const newPendingPast = Math.max(0, prevPendingExt.past - deltaPast);
      const newPendingFuture = Math.max(0, prevPendingExt.future - deltaFuture);
      setPendingExt({ past: newPendingPast, future: newPendingFuture });
      setView((prev) => {
        const prevEffectiveStart = prevS - prevPendingExt.past;
        const prevEffectiveEnd = prevE + prevPendingExt.future;
        const wasAtFullBounds =
          prev.start === prevEffectiveStart && prev.end === prevEffectiveEnd;
        if (!wasAtFullBounds) return prev;
        return {
          start: totalStart - newPendingPast,
          end: totalEnd + newPendingFuture,
        };
      });
    } else {
      setView({ start: totalStart, end: totalEnd });
    }
  }, [totalStart, totalEnd]);

  const effectiveStart = totalStart - pendingExt.past;
  const effectiveEnd = totalEnd + pendingExt.future;
  const effectiveDuration = effectiveEnd - effectiveStart;

  const clamp = useCallback(
    (start: number, end: number): ViewWindow => {
      const minDuration = Math.min(
        ZOOM_CONFIG.MIN_VISIBLE_DURATION_MS,
        effectiveDuration
      );
      const duration = Math.max(end - start, minDuration);
      let s = Math.max(
        effectiveStart,
        Math.min(start, effectiveEnd - duration)
      );
      const e = Math.min(effectiveEnd, s + duration);
      s = Math.max(effectiveStart, e - duration);
      return { start: s, end: e };
    },
    [effectiveStart, effectiveEnd, effectiveDuration]
  );

  const zoomAtCursor = useCallback(
    (cursorFraction: number, zoomIn: boolean) => {
      const visibleDuration = view.end - view.start;
      const minDuration = Math.min(
        ZOOM_CONFIG.MIN_VISIBLE_DURATION_MS,
        effectiveDuration
      );
      if (zoomIn && visibleDuration <= minDuration) {
        return;
      }
      const delta = visibleDuration * ZOOM_CONFIG.SCROLL_ZOOM_FACTOR;
      const newDuration = zoomIn
        ? visibleDuration - delta
        : visibleDuration + delta;

      if (newDuration >= effectiveDuration) {
        const atFullBounds =
          view.start === effectiveStart && view.end === effectiveEnd;
        if (!zoomIn && atFullBounds) {
          const cap = ZOOM_CONFIG.AUTO_LOAD_FETCH_WINDOW_MS;
          const stepPerSide = delta / 2;
          const newPast = Math.min(cap, pendingExt.past + stepPerSide);
          const newFuture = Math.min(cap, pendingExt.future + stepPerSide);
          const grew =
            newPast > pendingExt.past || newFuture > pendingExt.future;
          if (grew) {
            setPendingExt({ past: newPast, future: newFuture });
            setView({
              start: totalStart - newPast,
              end: totalEnd + newFuture,
            });
            onRequestExtendBounds?.(newPast, newFuture);
          }
          return;
        }
        setView({ start: effectiveStart, end: effectiveEnd });
        return;
      }

      const cursorTime = view.start + cursorFraction * visibleDuration;
      const newStart = cursorTime - cursorFraction * newDuration;
      setView(clamp(newStart, newStart + newDuration));
    },
    [
      clamp,
      effectiveStart,
      effectiveEnd,
      effectiveDuration,
      pendingExt,
      totalStart,
      totalEnd,
      view,
      onRequestExtendBounds,
    ]
  );

  const panByFraction = useCallback(
    (deltaFraction: number) => {
      setView((prev) => {
        const visibleDuration = prev.end - prev.start;
        const deltaMs =
          deltaFraction * visibleDuration * ZOOM_CONFIG.DRAG_PAN_SENSITIVITY;
        return clamp(prev.start + deltaMs, prev.end + deltaMs);
      });
    },
    [clamp]
  );

  const panToThumbLeft = useCallback(
    (newThumbLeftPercent: number) => {
      setView((prev) => {
        const visibleDuration = prev.end - prev.start;
        const newStart =
          effectiveStart + (newThumbLeftPercent / 100) * effectiveDuration;
        return clamp(newStart, newStart + visibleDuration);
      });
    },
    [clamp, effectiveStart, effectiveDuration]
  );

  const resetZoom = useCallback(() => {
    setPendingExt({ past: 0, future: 0 });
    setView({ start: totalStart, end: totalEnd });
  }, [totalStart, totalEnd]);

  const thumbWidthPercent =
    effectiveDuration > 0
      ? ((view.end - view.start) / effectiveDuration) * 100
      : 100;
  const thumbLeftPercent =
    effectiveDuration > 0
      ? ((view.start - effectiveStart) / effectiveDuration) * 100
      : 0;
  const isZoomed = view.start !== effectiveStart || view.end !== effectiveEnd;

  return {
    viewStart: view.start,
    viewEnd: view.end,
    thumbLeftPercent,
    thumbWidthPercent,
    isZoomed,
    zoomAtCursor,
    panByFraction,
    panToThumbLeft,
    resetZoom,
  };
}
